import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { Container, Typography, Button } from '@mui/material';

import { RichTextBlock } from 'components/ContentfulContent/RichText/RichTextBlock';
import { filterPageBreakMacro } from 'lib/legal-docs/helper';

import { Options } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS } from '@contentful/rich-text-types';

const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, '-')
    .replace(/^-|-$/g, '');
};

export interface LegalDocType {
  document?: Document;
  pdfLink?: string | null;
}

const StyledH2 = styled(Typography)`
  font-weight: 700;
  margin-top: 2.5rem;
  white-space: normal;
  overflow-wrap: anywhere;

  &:nth-of-type(1) {
    margin-top: 1rem;
  }
`;

export const LegalDoc = ({ document, pdfLink }: LegalDocType) => {
  const filteredDoc = filterPageBreakMacro(document);
  const overrideOptions = {
    renderNode: {
      [BLOCKS.HEADING_1]: (_, children) => (
        <Typography
          variant="h1"
          style={{
            fontWeight: 900,
            whiteSpace: 'normal',
            lineHeight: 1.4,
            overflowWrap: 'anywhere',
          }}
          color={'primary'}
        >
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_2]: (node, children) => {
        const textNode = node.content[0];
        const text = textNode && 'value' in textNode ? textNode.value : '';
        const id = generateSlug(text);
        return (
          <StyledH2 variant="h2" color={'primary'} id={id}>
            {children}
          </StyledH2>
        );
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        const textNode = node.content[0];
        const text = textNode && 'value' in textNode ? textNode.value : '';
        const id = generateSlug(text);
        return (
          <Typography
            variant="h3"
            style={{
              fontWeight: 700,
              marginTop: '1rem',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
            color={'primary'}
            id={id}
          >
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_4]: (_, children) => (
        <Typography
          variant="h4"
          style={{
            marginTop: '1rem',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
          color={'primary'}
        >
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_5]: (_, children) => (
        <Typography
          variant="h5"
          style={{
            marginTop: '1rem',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
          color={'primary'}
        >
          {children}
        </Typography>
      ),
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Typography
          variant="body1"
          style={{
            marginTop: '0.5rem',
            marginBottom: '1rem',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {children}
        </Typography>
      ),

      // Override UL to no longer strip the bullet points as the text macros are not used in legal docs
      [BLOCKS.UL_LIST]: (_, children) => (
        <ul style={{ paddingLeft: '1rem', whiteSpace: 'normal' }}>
          {children}
        </ul>
      ),
    },
  } as Options;

  return (
    <>
      {pdfLink && (
        <Container
          sx={{
            display: { xs: 'flex', md: 'block' },
            justifyContent: { xs: 'center' },
          }}
        >
          <Button
            variant="contained"
            href={pdfLink}
            target="_blank"
            sx={{
              float: { xs: 'none', md: 'right' },
              marginTop: '1rem',
              marginBottom: '.3rem',
              marginLeft: '.5rem',
            }}
          >
            <DownloadIcon fontSize="inherit" />
            &nbsp;PDF herunterladen
          </Button>
        </Container>
      )}

      <RichTextBlock document={filteredDoc} options={overrideOptions} />
    </>
  );
};
